/**
 * Value masking for the dashboard's hide mode.
 *
 * Every helper replaces the value it is given rather than obscuring it, so the
 * real figure never reaches the DOM. A CSS blur would leave it selectable,
 * copyable and present in any page export — no use for a screenshot, a
 * screen-share, or the landing-page captures.
 *
 * The mask is a fixed run of dots for the same reason: a mask that tracked the
 * value's length would show which station earns most without printing a digit.
 *
 * Figures and identifiers have no helper — they mask to {@link MASK} whatever
 * they hold, so call sites use the constant directly. Only emails and phones
 * need logic, because they keep a readable fragment.
 */

/** What every hidden value renders as. */
export const MASK = '••••••';

const DOT = '•';

/**
 * Hide an email, keeping the first three characters and the top-level domain.
 *
 * The visible head is deliberate: an operator scanning a masked table still
 * needs to tell one row from another. Anything shorter than the head is masked
 * whole rather than passed through.
 */
export function maskEmail(email: string): string {
  const at = email.indexOf('@');
  const dot = email.lastIndexOf('.');
  if (at <= 0 || dot <= at + 1) return MASK;

  const head = email.slice(0, 3);
  if (head.length < 3 || at < 3) return MASK;

  const local = DOT.repeat(Math.max(1, at - 3));
  const domain = DOT.repeat(3);
  return `${head}${local}@${domain}${email.slice(dot)}`;
}

/**
 * Hide a phone number, keeping only the country code.
 *
 * Formatted as the app writes them: `+380 (67) 134-45-26`. Anything else is
 * masked whole — a number we cannot parse is a number we cannot safely
 * partially reveal.
 */
export function maskPhone(phone: string): string {
  const match = /^(\+\d{1,4})\s*\(\d+\)/.exec(phone);
  if (!match) return MASK;

  return `${match[1]} (${DOT.repeat(2)}) ${DOT.repeat(3)}-${DOT.repeat(2)}-${DOT.repeat(2)}`;
}
